import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}

/**
 * The standard page-level header: title, optional description, optional right-aligned actions.
 *
 * <p><b>The actions stay on the right even when the text is long.</b> Both children are explicit flex
 * items rather than being left to their intrinsic widths: the title block grows and is allowed to
 * shrink (`minWidth: 0`, so a long description wraps INSIDE its own column instead of widening the
 * row), and the actions never shrink. Without that, a page with a multi-sentence description pushed
 * the buttons onto a line of their own — where `space-between` then left-aligned them, under the
 * title and on top of whatever filter row came next. The `320px` basis is the point below which the
 * two genuinely cannot share a line; past it they wrap, and `ml: auto` keeps them right-aligned there
 * too.</p>
 */
export function PageHeader({ title, description, actions, breadcrumb }: PageHeaderProps) {
  return (
    <Box sx={{ mb: 3 }}>
      {breadcrumb ? <Box sx={{ mb: 1 }}>{breadcrumb}</Box> : null}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        flexWrap="wrap"
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Typography variant="h4" component="h1">
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        {actions ? <Box sx={{ flexShrink: 0, ml: 'auto' }}>{actions}</Box> : null}
      </Stack>
    </Box>
  );
}
